import { useEffect, useRef, useState, type DragEvent, type FC } from 'react';
import { ActionIcon, Alert, Button, FileButton, Group, Loader, Paper, Stack, Text } from '@mantine/core';
import { IconAlertCircle, IconFileTypePdf, IconTrash } from '@tabler/icons-react';
import { ErrorNotificationMessage } from '~/shared/ui/ErrorNotificationMessage';
import {
    fileToRawBase64,
    formatReceiptFileSize,
    RECEIPT_FILE_ACCEPT,
    RECEIPT_FILE_READ_ERROR,
    RECEIPT_MIME_TYPE,
    RECEIPT_UNSUITABLE_ERROR,
    RECEIPT_UNSUITABLE_TITLE,
    RECEIPT_UNSUITABLE_WITH_TRACE_ERROR,
    validateReceiptFiles,
} from './lib';
import { type ReceiptSubmitResult, type ReceiptUploadProps } from './types';
import styles from './ReceiptUpload.module.css';

export const ReceiptUpload: FC<ReceiptUploadProps> = ({ onSubmit }) => {
    const [file, setFile] = useState<File | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [unsuitableResult, setUnsuitableResult] = useState<ReceiptSubmitResult | null>(null);

    const isSubmittingRef = useRef(false);
    const resetRef = useRef<(() => void) | null>(null);
    const attachButtonRef = useRef<HTMLButtonElement | null>(null);
    const replaceButtonRef = useRef<HTMLButtonElement | null>(null);
    const previousFileRef = useRef<File | null>(file);

    const unsuitableReason = unsuitableResult?.message?.trim()?.toLowerCase();

    const handleFiles = (files: readonly File[]) => {
        setUnsuitableResult(null);

        const validationError = validateReceiptFiles(files);

        if (validationError) {
            setError(validationError);

            return false;
        }

        setFile(files[0]);
        setError(null);

        return true;
    };

    const handleFileSelection = (selectedFile: File | null) => {
        if (isSubmittingRef.current) {
            return;
        }

        if (selectedFile && !handleFiles([selectedFile])) {
            resetRef.current?.();
        }
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();

        if (isSubmittingRef.current) {
            return;
        }

        setIsDragging(false);
        handleFiles(Array.from(event.dataTransfer.files));
    };

    const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
        if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) {
            return;
        }

        setIsDragging(false);
    };

    const handleRemove = () => {
        if (isSubmittingRef.current) {
            return;
        }

        resetRef.current?.();
        setFile(null);
        setError(null);
        setUnsuitableResult(null);
    };

    const handleSubmit = async () => {
        if (!file || !onSubmit || isSubmittingRef.current) {
            return;
        }

        const validationError = validateReceiptFiles([file]);

        if (validationError) {
            setError(validationError);

            return;
        }

        isSubmittingRef.current = true;
        setIsSubmitting(true);
        setIsDragging(false);
        setError(null);
        setUnsuitableResult(null);

        try {
            let base64: string;

            try {
                base64 = await fileToRawBase64(file);
            } catch {
                setError(RECEIPT_FILE_READ_ERROR);

                return;
            }

            try {
                const result = await onSubmit({
                    fileName: file.name,
                    mimeType: RECEIPT_MIME_TYPE,
                    size: file.size,
                    base64,
                });

                if (!result.suitable) {
                    setUnsuitableResult(result);
                }
            } catch {
                // HTTP and transport errors are shown by the request/entity error handlers.
            }
        } finally {
            isSubmittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        const previousFile = previousFileRef.current;

        previousFileRef.current = file;

        if (previousFile === file) {
            return;
        }

        if (file) {
            replaceButtonRef.current?.focus();

            return;
        }

        attachButtonRef.current?.focus();
    }, [file]);

    return (
        <Paper
            className={styles.root}
            bg='var(--app-accent-soft)'
            bd='1px solid var(--app-accent-border)'
        >
            <Stack gap='md'>
                <Group
                    className={styles.header}
                    justify='space-between'
                >
                    <Text fw={600}>Чек об оплате</Text>
                </Group>

                <div
                    className={styles.dropzone}
                    data-disabled={isSubmitting}
                    data-dragging={isDragging}
                    role='region'
                    aria-label='Зона загрузки чека'
                    onDragEnter={(event) => {
                        event.preventDefault();

                        if (!isSubmittingRef.current) {
                            setIsDragging(true);
                        }
                    }}
                    onDragLeave={handleDragLeave}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={handleDrop}
                >
                    <Stack gap='sm'>
                        {file ? (
                            <Group
                                className={styles.selectedFile}
                                gap='md'
                            >
                                <IconFileTypePdf
                                    className={styles.uploadIcon}
                                    aria-hidden='true'
                                    size={36}
                                />

                                <div className={styles.fileInfo}>
                                    <Text
                                        className={styles.fileName}
                                        fw={600}
                                        title={file.name}
                                    >
                                        {file.name}
                                    </Text>
                                    <Text
                                        c='dimmed'
                                        size='sm'
                                    >
                                        {formatReceiptFileSize(file.size)} · Готов к отправке
                                    </Text>
                                </div>

                                <Group
                                    className={styles.fileActions}
                                    gap='xs'
                                    wrap='nowrap'
                                >
                                    <FileButton
                                        accept={RECEIPT_FILE_ACCEPT}
                                        inputProps={{
                                            'aria-label': 'Выбрать PDF-чек',
                                            disabled: isSubmitting,
                                        }}
                                        resetRef={resetRef}
                                        onChange={handleFileSelection}
                                    >
                                        {(props) => (
                                            <Button
                                                {...props}
                                                ref={replaceButtonRef}
                                                aria-label='Заменить чек'
                                                disabled={isSubmitting}
                                                variant='light'
                                                onClick={() => {
                                                    if (isSubmittingRef.current) {
                                                        return;
                                                    }

                                                    resetRef.current?.();
                                                    props.onClick();
                                                }}
                                            >
                                                Заменить
                                            </Button>
                                        )}
                                    </FileButton>

                                    <ActionIcon
                                        aria-label='Удалить чек'
                                        disabled={isSubmitting}
                                        variant='subtle'
                                        onClick={handleRemove}
                                    >
                                        <IconTrash
                                            aria-hidden='true'
                                            size={20}
                                        />
                                    </ActionIcon>
                                </Group>
                            </Group>
                        ) : (
                            <Stack
                                align='center'
                                gap='xs'
                            >
                                <IconFileTypePdf
                                    className={styles.uploadIcon}
                                    aria-hidden='true'
                                    size={36}
                                />

                                <Text fw={600}>Перетащите PDF сюда</Text>

                                <Text
                                    c='dimmed'
                                    size='sm'
                                >
                                    PDF до 15 МБ
                                </Text>

                                <FileButton
                                    accept={RECEIPT_FILE_ACCEPT}
                                    inputProps={{
                                        'aria-label': 'Выбрать PDF-чек',
                                        disabled: isSubmitting,
                                    }}
                                    resetRef={resetRef}
                                    onChange={handleFileSelection}
                                >
                                    {(props) => (
                                        <Button
                                            {...props}
                                            ref={attachButtonRef}
                                            disabled={isSubmitting}
                                        >
                                            Прикрепить чек
                                        </Button>
                                    )}
                                </FileButton>
                            </Stack>
                        )}

                        {unsuitableResult && (
                            <Alert
                                aria-label={RECEIPT_UNSUITABLE_TITLE}
                                classNames={{
                                    body: styles.alertBody,
                                    icon: styles.alertIcon,
                                    message: styles.alertMessage,
                                    root: styles.unsuitableAlert,
                                }}
                                color='red'
                                icon={
                                    <IconAlertCircle
                                        aria-hidden='true'
                                        size={18}
                                    />
                                }
                                role='alert'
                                title={RECEIPT_UNSUITABLE_TITLE}
                                variant='light'
                            >
                                <Stack gap='xs'>
                                    {unsuitableReason && (
                                        <Text
                                            c='dimmed'
                                            size='sm'
                                        >
                                            <Text
                                                component='span'
                                                inherit
                                                fw={600}
                                            >
                                                Причина:
                                            </Text>{' '}
                                            {unsuitableReason}
                                        </Text>
                                    )}

                                    <ErrorNotificationMessage
                                        message={
                                            unsuitableResult.traceId
                                                ? RECEIPT_UNSUITABLE_WITH_TRACE_ERROR
                                                : RECEIPT_UNSUITABLE_ERROR
                                        }
                                        traceId={unsuitableResult.traceId}
                                    />
                                </Stack>
                            </Alert>
                        )}

                        {error && (
                            <Text
                                c='red'
                                role='alert'
                                size='sm'
                                ta='center'
                            >
                                {error}
                            </Text>
                        )}
                    </Stack>
                </div>

                <Button
                    aria-busy={isSubmitting}
                    disabled={!file || !onSubmit || isSubmitting}
                    fullWidth
                    leftSection={
                        isSubmitting ? (
                            <Loader
                                aria-hidden='true'
                                color='currentColor'
                                data-testid='receipt-submit-loader'
                                size='xs'
                            />
                        ) : undefined
                    }
                    onClick={() => void handleSubmit()}
                >
                    {isSubmitting ? 'Проверяем чек...' : 'Отправить чек'}
                </Button>
            </Stack>
        </Paper>
    );
};
